import {
  Container,
  Header,
  SpaceBetween,
  ColumnLayout,
  Input,
  Select,
  FormField,
  TextContent,
  Box,
} from "@cloudscape-design/components-themed";
import { COMMENT_MAX_LENGTH, LABELS } from "./constants";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { isHalfNumberValid, isLengthValid } from "../../utils/inputValidation";
import { getMessage } from "../../features/error/messages";
import {
  ConfirmationMerchantInfo,
  CSDict,
} from "../../features/api-interfaces";
import { addBlank, CODE_MASTER } from "../default-constants";
import { useCodelistHook } from "../../app/useCodelistHook";
import {
  getApiConfirmationResult,
  getComment,
  getConfirmationResultDict,
  getRateCategoryCodeDict,
  getTargetRateItems,
} from "./util";
import { useGetIndividualHandlingConfirmationTargetRateQuery } from "../../features/api";
import { getIHRLabel } from "../../utils/common";

interface IMerchant {
  merchant: ConfirmationMerchantInfo;
  setMerchant: Dispatch<SetStateAction<ConfirmationMerchantInfo | undefined>>;
  isMerchantRegistered: boolean;
  isIndividualConsultation: boolean;
}

/**
 * 個別取扱確認更新 加盟店
 */
const Merchant = (props: IMerchant) => {
  const {
    merchant,
    setMerchant,
    isMerchantRegistered,
    isIndividualConsultation,
  } = props;
  const { individualHandlingReason } = useCodelistHook();
  const [rateCategoryItems, setRateCategoryItems] = useState<CSDict[]>([]);
  const [commentErrorMsg, setCommentErrorMsg] = useState("");
  const [
    individualConsultationNoErrorMsg,
    setIndividualConsultationNoErrorMsg,
  ] = useState("");
  const [
    individualConsultationBranchNoErrorMsg,
    setIndividualConsultationBranchNoErrorMsg,
  ] = useState("");
  const CONFIRMATION_RESULT = addBlank(CODE_MASTER["CONFIRMATION_RESULT"]);
  const { data: targetRates } =
    useGetIndividualHandlingConfirmationTargetRateQuery();

  useEffect(() => {
    setRateCategoryItems(addBlank(getTargetRateItems(targetRates)));
  }, [targetRates]);

  /**
   * コメントの入力ハンドラ
   * @param {string} inputValue
   */
  const commentOnChange = (inputValue: string) => {
    setMerchant({
      ...merchant,
      comment: inputValue,
    });
    const errorMsg = isLengthValid(inputValue, COMMENT_MAX_LENGTH)
      ? ""
      : getMessage("KGRERR058", COMMENT_MAX_LENGTH).label;
    setCommentErrorMsg(errorMsg);
  };

  /**
   * 個別相談-Noの入力ハンドラ.
   * @param {string} inputValue
   */
  const checkIndividualConsultationNo = (inputValue: string) => {
    setMerchant({
      ...merchant,
      individualConsultationNo: Number(inputValue),
    });
    const errorMsg = isHalfNumberValid(inputValue)
      ? ""
      : getMessage("KGRERR006", LABELS.INDIVIDUAL_CONSULTATION_NO).label;
    setIndividualConsultationNoErrorMsg(errorMsg);
  };

  /**
   * 個別相談-枝番の入力ハンドラ.
   * @param {string} inputValue
   */
  const individualConsultationBranchNoOnChange = (inputValue: string) => {
    setMerchant({
      ...merchant,
      individualConsultationBranchNo: Number(inputValue),
    });
    const errorMsg = isHalfNumberValid(inputValue)
      ? ""
      : getMessage("KGRERR006", LABELS.INDIVIDUAL_CONSULTATION_BRANCH_NO).label;
    setIndividualConsultationBranchNoErrorMsg(errorMsg);
  };

  return (
    <Container header={<Header>{LABELS.MERCHANT}</Header>}>
      <SpaceBetween direction={"vertical"} size={"s"}>
        <Box>
          <Box margin={"s"} float={"left"}>
            <TextContent>
              <h5>{LABELS.INDIVIDUAL_HANDLING_REASON}</h5>
            </TextContent>
          </Box>
          <ColumnLayout columns={2}>
            <FormField>
              <Input
                value={getIHRLabel(
                  individualHandlingReason,
                  merchant.individualHandlingReason
                )}
                readOnly
              />
            </FormField>
          </ColumnLayout>
        </Box>
        <SpaceBetween direction={"horizontal"} size={"s"}>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.CONFIRMATION_RESULT}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={1}>
              <FormField constraintText={LABELS.BLANK_LITTLE_LONG}>
                <Select
                  selectedOption={getConfirmationResultDict(
                    merchant.confirmationResult
                  )}
                  options={CONFIRMATION_RESULT}
                  filteringType={"none"}
                  onChange={({ detail: { selectedOption } }) =>
                    setMerchant({
                      ...merchant,
                      confirmationResult: getApiConfirmationResult(
                        selectedOption.value!
                      ),
                      comment: getComment(selectedOption.value),
                    })
                  }
                  disabled={isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.COMMENT}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={0}>
              <FormField
                constraintText={
                  isMerchantRegistered
                    ? LABELS.BLANK_LONG
                    : `${COMMENT_MAX_LENGTH}文字以内${LABELS.BLANK_SUPER_LONG}`
                }
                errorText={commentErrorMsg}
              >
                <Input
                  value={merchant.comment ?? ""}
                  onChange={({ detail }) => {
                    commentOnChange(detail.value);
                  }}
                  disabled={isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
        </SpaceBetween>
        <SpaceBetween direction={"horizontal"} size={"s"}>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.RATE_CATEGORY}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={1}>
              <FormField constraintText={LABELS.BLANK_MIDDLE_LONG}>
                <Select
                  selectedOption={getRateCategoryCodeDict(
                    rateCategoryItems,
                    merchant.rateCategoryCode
                  )}
                  options={rateCategoryItems}
                  filteringType={"none"}
                  onChange={({ detail }) => {
                    setMerchant({
                      ...merchant,
                      rateCategoryCode:
                        detail.selectedOption.value == ""
                          ? null
                          : Number(detail.selectedOption.value),
                    });
                  }}
                  disabled={isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.INDIVIDUAL_CONSULTATION_NO}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={1}>
              <FormField
                constraintText={LABELS.BLANK_MIDDLE_LONG}
                errorText={individualConsultationNoErrorMsg}
              >
                <Input
                  value={String(merchant.individualConsultationNo ?? "")}
                  onChange={({ detail }) =>
                    checkIndividualConsultationNo(detail.value)
                  }
                  disabled={!isIndividualConsultation || isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
          <Box>
            <Box margin={"s"} float={"left"}>
              <TextContent>
                <h5>{LABELS.INDIVIDUAL_CONSULTATION_BRANCH_NO}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={1}>
              <FormField
                constraintText={LABELS.BLANK_MIDDLE_LONG}
                errorText={individualConsultationBranchNoErrorMsg}
              >
                <Input
                  value={String(merchant.individualConsultationBranchNo ?? "")}
                  onChange={({ detail }) =>
                    individualConsultationBranchNoOnChange(detail.value)
                  }
                  disabled={!isIndividualConsultation || isMerchantRegistered}
                />
              </FormField>
            </ColumnLayout>
          </Box>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

export default Merchant;
